'use client';

import { CopyInputValue } from '@/shared/components/ui/input/copy/copy-input-value.component';
import { useInputStyles } from '@/shared/components/ui/input/input.styles';
import { ShowPasswordButton } from '@/shared/components/ui/input/password/show-password-button.component';
import { useInputStore } from '@/shared/stores/ui/input/use-input.store';
import * as Phosphor from '@phosphor-icons/react';
import { usePathname } from 'next/navigation';
import { FC, InputHTMLAttributes, ReactNode, useEffect, useState } from 'react';
import { twJoin } from 'tailwind-merge';

const { SSR, IconContext, ...PhosphorIcon } = Phosphor;

/**
 * @todo Ver se não tem nenhuma forma melhor de reutilizar a interface do InputProps.
 */
export type InputPartialProps = Omit<InputProps, 'id' | 'label'> & {
  id?: string;
  label?: string;
};

type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  id: string;
  label: string;
  error?: string;
  copy?: string;
  container?: {
    className?: string;
  };
  hoverAnimations?: boolean;
  startIcon?: keyof typeof PhosphorIcon;
  endIcon?: keyof typeof PhosphorIcon;
  info?: {
    InfoDialog: (props: { isOpen: boolean; onClose: () => void }) => ReactNode;
  };
  description?: string
};

/**
 * Representa um input da aplicação.
 *
 * OBS Para inputs de senha: o estado da visualização da senha lida globalmente, ou seja, quando altera a visibilidade pra um input, alterada para todos. Não foi feita nenhuma modificação, pois é um comportamento que ajuda com a redução de clicks no momentos, mas caso se torne um problema será necessário realizar uma manutenção.
 *
 * @todo Ideia: armazenar dados do formulário, criptografados no local storage, pra caso aconteça algum imprevisto e a página recarregar, os valores inseridos nos inputs não serão perdidos.
 */
export const Input: FC<InputProps> = ({
  id,
  label,
  type,
  className,
  error,
  container,
  hoverAnimations = false,
  copy,
  startIcon,
  endIcon,
  info,
  description,
  ...rest
}) => {
  const { InfoDialog } = info ? info : {};

  const { inputStyle, labelStyle } = useInputStyles();
  const { showPassword, setShowPassword } = useInputStore();
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);

  const pathname = usePathname();

  // Reseta a visibilidade dos inputs de senha quando ocorre uma alteração de página.
  useEffect(() => {
    if (showPassword) {
      setShowPassword(false);
    }
  }, [pathname]);

  const EndIcon = endIcon ? PhosphorIcon[endIcon] : undefined;
  const StartIcon = startIcon ? PhosphorIcon[startIcon] : undefined;

  const openInfoDialog = () => {
    setInfoDialogOpen(true);
  };
  const closeInfoDialog = () => {
    setInfoDialogOpen(false);
  };

  return (
    <div
      className={twJoin('relative flex w-full flex-col', container?.className)}
    >
      <div className="relative flex flex-col">
        <input
          id={id}
          type={
            type === 'password' ? (showPassword ? 'text' : 'password') : type
          }
          className={twJoin(
            inputStyle({ danger: !!error, password: type === 'password' }),
            copy ? 'pr-16' : '',
            StartIcon ? 'pl-12' : '',
            EndIcon ? 'pr-12' : '',
            'overflow-hidden truncate',
            className,
          )}
          placeholder=" "
          {...rest}
        />
        <label
          htmlFor={id}
          className={labelStyle({
            danger: !!error,
            hoverAnimations,
          })}
        >
          {label}
          {info && (
            <button
              type="button"
              className={twJoin(
                'rounded-full',
                'transition-all duration-300',
                'outline-none',
                'focus:ring-2 focus:ring-primary focus:ring-offset-2',
                // 'hover:ring-2 hover:ring-primary hover:ring-offset-2',
              )}
              onClick={openInfoDialog}
            >
              <PhosphorIcon.QuestionMark
                size={18}
                className="rounded-full bg-primary p-[2px] text-white"
                weight="bold"
              />
            </button>
          )}
        </label>
        {type === 'password' && <ShowPasswordButton />}
        {copy && <CopyInputValue value={copy} />}
        {StartIcon && (
          <StartIcon
            size={25}
            // @ts-ignore
            weights="regular"
            className={twJoin(
              'absolute left-[16px] top-1/2 -translate-y-1/2',
              'text-gray-400',
            )}
          />
        )}
        {EndIcon && (
          <EndIcon
            size={25}
            // @ts-ignore
            weights="regular"
            className={twJoin(
              'absolute right-[16px] top-1/2 -translate-y-1/2',
              'text-gray-400',
            )}
          />
        )}
        {InfoDialog && (
          <InfoDialog isOpen={infoDialogOpen} onClose={closeInfoDialog} />
        )}
      </div>
      {description && (
        <span className="mt-1 text-left text-sm text-gray-400">{description}</span>
      )}
      {error && (
        <span className="mt-1 text-left text-sm text-red-500">{error}</span>
      )}
    </div>
  );
};
